import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    makeStyles,
    Tooltip,
    Typography
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useTranslation } from "react-i18next";
import Page from 'components/Page';
import { useLocation } from "react-router-dom";
import { getShipperLogoPath } from 'helpers';
import { useNavigate } from "react-router-dom";
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import SecureFetchContext from 'context/SecureFetchContext';
import getApiRoute from 'routes/apiRoutes';
import { SHIPPER_SHORTCUTS } from 'constants/shippers';
import AppContext from 'context/AppContext';

const useStyles = makeStyles(() => ({
    root: {},
    card: {
        boxShadow: 'rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px'
    }
}));

const NewPackageImport = ({ className, shipper, handlePackageClose, ...rest }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { state } = useLocation();
    const secureFetch = React.useContext(SecureFetchContext);
    const { appValues } = React.useContext(AppContext);
    const [userSettings, setUserSettings] = React.useState({});
    const [importDefaults, setImportDefaults] = React.useState(state.importDefaults || {});
    let navigate = useNavigate();

    const isSkpost = state.shipper.shortcut.toUpperCase() === SHIPPER_SHORTCUTS.SKPOST;
    const userShipperPriceList = appValues.user?.user_shipper_price_lists?.find(
        uspl => uspl.shipper.id === parseInt(state.shipper.id)
    );
    const isPremiumSkpost = isSkpost && !!userShipperPriceList && userShipperPriceList.sale_price_list?.type !== 'fix';

    React.useEffect(() => {
        if (!isSkpost) {
            return;
        }

        secureFetch(getApiRoute('usersettings', [state.shipper.id])).then((json) => {
            const settings = json || {};
            const receptionOptions = settings[`${SHIPPER_SHORTCUTS.SKPOST}_RECEPTION_METHOD_OPTIONS`] || [];
            const paymentOptions = settings[`${SHIPPER_SHORTCUTS.SKPOST}_PAYMENT_TYPE_CODE_OPTIONS`] || [];
            setUserSettings(settings);
            setImportDefaults((prev) => {
                const next = {
                    ...prev,
                    others_reception_method_fix: prev.others_reception_method_fix || receptionOptions.find(o => o.code === 'post')?.code || receptionOptions[0]?.code || '',
                };

                if (isPremiumSkpost) {
                    delete next.others_payment_type_code_fix;
                } else {
                    next.others_payment_type_code_fix = prev.others_payment_type_code_fix || paymentOptions.find(o => o.code === 'up')?.code || paymentOptions[0]?.code || '';
                }

                return next;
            });
        });
    }, [isPremiumSkpost, isSkpost, secureFetch, state.shipper.id]);

//    to = { '/app/package/upload/'+shipper.id }

    const imports = [
        {
            alt: 'csv',
            image: 'csv-file.svg',
            condition: (shipper) => shipper.shortcut.toUpperCase() !== 'PACKETA'
        },
        {
            alt: 'xml',
            image: 'xml-file.svg',
            condition: (shipper) => shipper.shortcut.toUpperCase() !== 'PACKETA'
        },
        {
            alt: 'omega',
            image: 'omega.png',
            condition: (shipper) => shipper.shortcut.toUpperCase() !== 'PACKETA'
        },
        {
            alt: 'pohoda',
            image: 'pohoda.png',
            condition: (shipper) => shipper.shortcut.toUpperCase() !== 'PACKETA'
        },
        {
            alt: 'biznisweb',
            image: 'biznisweb.png',
            condition: (shipper) => shipper.shortcut.toUpperCase() !== 'PACKETA'
        },
        {
            alt: 'packeta',
            image: 'packeta_csv.png',
            condition: (shipper) => shipper.shortcut.toUpperCase() === 'PACKETA'
        },
        {
            alt: 'csv-gls-parcelshop',
            image: 'csv-gls-parcelshop.png',
            condition: (shipper) => shipper.shortcut.toUpperCase() === 'GLS'
        },
        {
            alt: 'shoptet-xml',
            image: 'shoptet_logo.png',
            tooltip: 'help.shoptet_xml_import_instruction'
        },
    ];

    const getOptions = (key) => userSettings[key] || [];
    const getSelectedOption = (key, value) => getOptions(key).find((option) => option.code === value) || null;
    const handleDefaultChange = (key, option) => {
        setImportDefaults({
            ...importDefaults,
            [key]: option ? option.code : '',
        });
    };
    const getImportState = () => {
        const defaults = {...importDefaults};
        if (isPremiumSkpost) {
            delete defaults.others_payment_type_code_fix;
        }
        return {...state, hidePaymentTypeCode: isPremiumSkpost, importDefaults: defaults};
    };

    return (
        <Page
            title={t('title.import_packages') + ' - ' + state.shipper.shortcut}
        >
            <Container maxWidth={false}>
                <Typography variant="h1" gutterBottom >
                    <img src={getShipperLogoPath(state.shipper.shortcut)} alt={state.shipper.shortcut} height={30} />
                    &nbsp;&nbsp;
                    {t('title.import_packages')}
                </Typography>
                {isSkpost && (
                    <Box mb={3}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <Autocomplete
                                            options={getOptions(`${SHIPPER_SHORTCUTS.SKPOST}_RECEPTION_METHOD_OPTIONS`)}
                                            value={getSelectedOption(`${SHIPPER_SHORTCUTS.SKPOST}_RECEPTION_METHOD_OPTIONS`, importDefaults.others_reception_method_fix)}
                                            onChange={(event, option) => handleDefaultChange('others_reception_method_fix', option)}
                                            getOptionLabel={(option) => option.label}
                                            getOptionSelected={(option, value) => option.code === value.code}
                                            renderInput={(params) => (
                                                <TextField {...params} label={`${t('label.default')}: ${t('label.others_reception_method')}`} />
                                            )}
                                        />
                                    </Grid>
                                    {!isPremiumSkpost && (
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                options={getOptions(`${SHIPPER_SHORTCUTS.SKPOST}_PAYMENT_TYPE_CODE_OPTIONS`)}
                                                value={getSelectedOption(`${SHIPPER_SHORTCUTS.SKPOST}_PAYMENT_TYPE_CODE_OPTIONS`, importDefaults.others_payment_type_code_fix)}
                                                onChange={(event, option) => handleDefaultChange('others_payment_type_code_fix', option)}
                                                getOptionLabel={(option) => option.label}
                                                getOptionSelected={(option, value) => option.code === value.code}
                                                renderInput={(params) => (
                                                    <TextField {...params} label={`${t('label.default')}: ${t('label.others_payment_type_code')}`} />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}
                <Grid container spacing={3}>
                    {
                        imports.map(v =>
                            (!v.condition || v.condition(state.shipper)) &&
                            <Grid item className={classes.item} key={v.alt}>
                                <Card className={classes.card}>
                                    <CardContent>
                                        <Box textAlign="center" position="relative">
                                            {v.tooltip && (
                                                <Tooltip title={t(v.tooltip)}>
                                                    <IconButton size="small" style={{ position: 'absolute', top: 0, right: 0 }} aria-label={t(v.tooltip)}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <img src={"/images/" + v.image} alt={v.alt} height={80} width={140} style={{ objectFit: "contain" }} />
                                        </Box>
                                    </CardContent>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={() => navigate(`/app/package/import/${v.alt}`, {state: getImportState()})}
                                    >
                                        {t('button.import')}
                                    </Button>
                                </Card>
                            </Grid>
                        )
                    }
                </Grid>
            </Container>
        </Page>
    );
};

export default NewPackageImport;
